import { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, FormControlLabel, MenuItem,
  Snackbar, Alert, Switch, TextField, Typography,
  Divider,
} from '@mui/material';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../components/shared/ConfirmDialog';
import { Add, ViewList, DeleteOutline, Terrain } from '@mui/icons-material';
import { EditableGrid } from '../../../components/tables/EditableGrid';
import { quoteLineItemColumns } from './quoteLineItemColumns';
import { CataloguePickerModal } from './CataloguePickerModal';
import { QuoteLinePrereqChips } from './QuoteLinePrereqChips';
import { taxTypes } from '../../../constants/statusOptions';
import { ROCK_TESTING_SUITE } from '../../../constants/quoteSuites';
import { getTestCatalogItems } from '../../../services/testCatalogService';
import {
  createQuoteLineItem,
  updateQuoteLineItem,
  deleteQuoteLineItem,
  addLineItemsFromCatalog,
} from '../../../services/quoteService';
import {
  getQuotePrerequisiteStatus,
  type PrereqStatusRow,
  type PrereqDetail,
} from '../../../services/quotePrerequisites';
import type { QuoteDetail, QuoteLineItem } from '../../../types/quote';
import { apiErrorText } from '../../../utils/apiErrorText';

interface Props {
  quote: QuoteDetail;
  onUpdate: (data: Record<string, unknown>) => void;
  onRefresh: () => void;
  readOnly?: boolean;
}

export const QuotePricingSection = ({ quote, onUpdate, onRefresh, readOnly }: Props) => {
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' });
  const [suiteAdding, setSuiteAdding] = useState(false);
  const [showDiscount, setShowDiscount] = useState((quote.DiscountAmount ?? 0) > 0 || (quote.DiscountValue ?? 0) > 0);
  const [taxType, setTaxType] = useState(quote.TaxType ?? 'None');
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  useEffect(() => { setTaxType(quote.TaxType ?? 'None'); }, [quote.TaxType]);

  const [localItems, setLocalItems] = useState<Record<string, unknown>[]>([]);
  useEffect(() => {
    setLocalItems((quote.lineItems ?? []).map((li) => ({ ...li })));
  }, [quote.lineItems]);

  // ── Prerequisite status ───────────────────────────────────────────────────
  const [prereqStatus, setPrereqStatus] = useState<PrereqStatusRow[]>([]);

  const loadPrereqStatus = useCallback(async () => {
    if (!quote.QuoteKey) return;
    try {
      const data = await getQuotePrerequisiteStatus(quote.QuoteKey);
      setPrereqStatus(data);
    } catch {
      // Prerequisite status is non-critical; silently ignore on error.
    }
  }, [quote.QuoteKey]);

  // Reload prereq status whenever line items change.
  useEffect(() => {
    loadPrereqStatus();
  }, [loadPrereqStatus, quote.lineItems]);

  // Build a map from lineItemKey → PrereqStatusRow for fast lookup in the panel.
  const prereqByLineItem = new Map<string, PrereqStatusRow>(
    prereqStatus.map((row) => [row.lineItemKey, row]),
  );

  // Rows that have at least one prerequisite (to show in the prereq panel).
  const rowsWithPrereqs = prereqStatus.filter((row) => row.prerequisites.length > 0);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleRowChange = useCallback((rowIndex: number, field: string, value: unknown) => {
    setLocalItems((prev) => {
      const next = [...prev];
      next[rowIndex] = { ...next[rowIndex], [field]: value };
      return next;
    });
  }, []);

  const handleRowSave = useCallback(async (row: Record<string, unknown>) => {
    const key = row.QuoteLineItemKey as string;
    if (!key) return;
    try {
      const updated = await updateQuoteLineItem(key, {
        Description: row.Description as string,
        AstmCode: row.AstmCode as string,
        LabNote: row.LabNote as string,
        Quantity: Number(row.Quantity) || 1,
        UnitPrice: Number(row.UnitPrice) || 0,
        LineDiscountPercent: Number(row.LineDiscountPercent) || 0,
      } as Partial<QuoteLineItem>);
      if (updated) {
        setLocalItems((prev) =>
          prev.map((li) => (li.QuoteLineItemKey === key ? { ...li, LineTotal: updated.LineTotal } : li)),
        );
      }
      onRefresh();
    } catch {
      setSnackbar({ open: true, message: 'Failed to save', severity: 'error' });
    }
  }, [onRefresh]);

  const handleAddLine = useCallback(async () => {
    try {
      await createQuoteLineItem(quote.QuoteKey, { LineType: 'Test', Description: '', Quantity: 1, UnitPrice: 0 });
      onRefresh();
    } catch { /* */ }
  }, [quote.QuoteKey, onRefresh]);

  const handleAddFromCatalog = useCallback(async (keys: string[]) => {
    try {
      await addLineItemsFromCatalog(quote.QuoteKey, keys);
      setCatalogOpen(false);
      onRefresh();
    } catch { /* */ }
  }, [quote.QuoteKey, onRefresh]);

  /**
   * FEAT-2026-0016 — one-click add of the rock testing suite (UCS, UCSM, TCS,
   * BTS, rock DS). Resolves the suite's canonical test names against the live
   * catalog at click time, then reuses the same from-catalog endpoint as the
   * picker so pricing/quantity/assumptions stay server-authoritative.
   */
  const handleAddRockSuite = useCallback(async () => {
    setSuiteAdding(true);
    try {
      const catalog = await getTestCatalogItems();
      const active = catalog.filter((i) => i.IsActive);
      const foundKeys: string[] = [];
      const missing: string[] = [];
      for (const name of ROCK_TESTING_SUITE.testNames) {
        const match = active.find((i) => i.TestName.trim().toLowerCase() === name.toLowerCase());
        if (match) foundKeys.push(match.TestCatalogItemKey);
        else missing.push(name);
      }
      if (foundKeys.length === 0) {
        setSnackbar({ open: true, message: `No ${ROCK_TESTING_SUITE.label} tests found in the catalogue`, severity: 'error' });
        return;
      }
      await addLineItemsFromCatalog(quote.QuoteKey, foundKeys);
      onRefresh();
      setSnackbar({
        open: true,
        message: missing.length
          ? `Added ${foundKeys.length} of ${ROCK_TESTING_SUITE.testNames.length} suite tests — not in catalogue: ${missing.join(', ')}`
          : `${ROCK_TESTING_SUITE.label} added (${foundKeys.length} tests)`,
        severity: missing.length ? 'warning' : 'success',
      });
    } catch (err) {
      setSnackbar({ open: true, message: apiErrorText(err, `Failed to add ${ROCK_TESTING_SUITE.label}`), severity: 'error' });
    } finally {
      setSuiteAdding(false);
    }
  }, [quote.QuoteKey, onRefresh]);

  const handleDeleteRow = useCallback(async (row: Record<string, unknown>) => {
    try {
      await deleteQuoteLineItem(row.QuoteLineItemKey as string);
      setLocalItems((prev) => prev.filter((li) => li.QuoteLineItemKey !== row.QuoteLineItemKey));
      onRefresh();
    } catch (err) {
      setSnackbar({ open: true, message: apiErrorText(err, 'Failed to delete line item'), severity: 'error' });
    }
  }, [onRefresh]);

  const handleBulkDelete = useCallback(async () => {
    if (!selectedKeys.length) return;
    try {
      await Promise.all(selectedKeys.map((k) => deleteQuoteLineItem(k)));
      setLocalItems((prev) => prev.filter((li) => !selectedKeys.includes(li.QuoteLineItemKey as string)));
      setSelectedKeys([]);
      onRefresh();
      setSnackbar({ open: true, message: `Deleted ${selectedKeys.length} line(s)`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: apiErrorText(err, 'Failed to delete'), severity: 'error' });
    }
  }, [selectedKeys, onRefresh]);

  /** Add a missing prerequisite to the quote as a new catalog line item. */
  const handleAddPrerequisite = useCallback(async (prereq: PrereqDetail) => {
    try {
      await addLineItemsFromCatalog(quote.QuoteKey, [prereq.requiredTestCatalogItemKey]);
      onRefresh();
      setSnackbar({
        open: true,
        message: `"${prereq.requiredTestName}" added to quote.`,
        severity: 'success',
      });
    } catch {
      setSnackbar({ open: true, message: 'Failed to add prerequisite', severity: 'error' });
    }
  }, [quote.QuoteKey, onRefresh]);

  const pmEnabled = (quote.PmFeePercent ?? 0) > 0;
  const quoteCurrency = (quote.Currency ?? 'CAD').toUpperCase();
  const isNonCad = quoteCurrency !== 'CAD';

  return (
    <Box>
      {!readOnly && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
          <Button variant="outlined" size="small" startIcon={<ViewList />}
            onClick={() => setCatalogOpen(true)} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
            Add from Catalogue
          </Button>
          <Button variant="outlined" size="small" startIcon={<Add />}
            onClick={handleAddLine} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
            Add Custom Line
          </Button>
          <Button variant="outlined" size="small" startIcon={<Terrain />}
            onClick={handleAddRockSuite} disabled={suiteAdding}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
            {suiteAdding ? 'Adding...' : `Add ${ROCK_TESTING_SUITE.label}`}
          </Button>
          {selectedKeys.length > 0 && (
            <Button variant="outlined" size="small" color="error" startIcon={<DeleteOutline />}
              onClick={() => setConfirmDlg({
                open: true,
                title: 'Delete Line Items',
                message: `Delete ${selectedKeys.length} selected line item${selectedKeys.length > 1 ? 's' : ''}? This cannot be undone.`,
                confirmLabel: 'Delete',
                confirmColor: 'error',
                onConfirm: handleBulkDelete,
              })}
              sx={{ textTransform: 'none', fontSize: '0.75rem', ml: 'auto' }}>
              Delete Selected ({selectedKeys.length})
            </Button>
          )}
        </Box>
      )}

      {isNonCad && (
        <Alert severity="info" sx={{ mb: 1, py: 0, fontSize: '0.75rem', '& .MuiAlert-message': { py: 0.5 } }}>
          Line-item prices are entered and stored in CAD. This quote is presented in {quoteCurrency} —
          the summary and PDF totals are converted at the locked FX rate
          {quote.FxRateAtQuote != null ? ` (${quote.FxRateAtQuote.toFixed(4)} CAD per ${quoteCurrency})` : ''}.
        </Alert>
      )}

      <EditableGrid columns={quoteLineItemColumns} data={localItems} keyField="QuoteLineItemKey"
        onRowChange={handleRowChange} onRowSave={handleRowSave} onSelectionChange={setSelectedKeys}
        allowAdd={false} onAddRow={() => {}} onRowDelete={readOnly ? undefined : handleDeleteRow}
        readOnly={readOnly} />

      {/* ── Prerequisite panel ─────────────────────────────────────────────── */}
      {rowsWithPrereqs.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
            Prerequisites
          </Typography>
          <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {rowsWithPrereqs.map((row) => {
              // Find the display name from localItems (may have been edited).
              const localItem = localItems.find(
                (li) => li.QuoteLineItemKey === row.lineItemKey,
              );
              const displayName = (localItem?.Description as string | undefined) ?? row.testName ?? '—';
              const prereqRow = prereqByLineItem.get(row.lineItemKey);
              if (!prereqRow) return null;
              return (
                <Box
                  key={row.lineItemKey}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                >
                  <Typography
                    variant="caption"
                    sx={{ minWidth: 160, maxWidth: 220, fontSize: '0.72rem', color: 'text.secondary', flexShrink: 0 }}
                  >
                    {displayName}
                  </Typography>
                  <QuoteLinePrereqChips
                    prerequisites={prereqRow.prerequisites}
                    onAddPrerequisite={handleAddPrerequisite}
                    readOnly={readOnly}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Quote-level settings */}
      <Box sx={{ display: 'flex', gap: 3, mt: 2.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControlLabel
          control={<Switch size="small" checked={pmEnabled} disabled={readOnly}
            onChange={(e) => onUpdate({ PmFeePercent: e.target.checked ? 10 : 0 })} />}
          label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Include 10% PM Fee</Typography>}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={showDiscount} disabled={readOnly}
            onChange={(e) => {
              setShowDiscount(e.target.checked);
              if (e.target.checked) {
                onUpdate({ DiscountType: 'Percent', DiscountValue: quote.DiscountValue || 0 });
              } else {
                onUpdate({ DiscountType: 'None', DiscountValue: 0 });
              }
            }} />}
          label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Apply Discount</Typography>}
        />
        {showDiscount && (
          <>
            <TextField select label="Type" value={quote.DiscountType ?? 'Percent'}
              onChange={(e) => onUpdate({ DiscountType: e.target.value, DiscountValue: quote.DiscountValue || 0 })}
              size="small" sx={{ width: 110 }} disabled={readOnly}>
              <MenuItem value="Percent">Percent</MenuItem>
              <MenuItem value="Fixed">Fixed $</MenuItem>
            </TextField>
            <TextField label={quote.DiscountType === 'Fixed' ? 'Amount ($)' : 'Discount %'} type="number"
              key={`disc-${quote.DiscountValue}`}
              defaultValue={quote.DiscountValue ?? 0}
              onBlur={(e) => {
                const v = parseFloat(e.target.value) || 0;
                onUpdate({ DiscountType: quote.DiscountType || 'Percent', DiscountValue: v });
              }}
              size="small" sx={{ width: 100 }} disabled={readOnly} />
          </>
        )}
        <TextField select label="Tax" value={taxType} disabled={readOnly}
          onChange={(e) => {
            const t = e.target.value;
            setTaxType(t);
            onUpdate({ TaxType: t, TaxPercent: t === 'GST' ? 5 : t === 'HST' ? 15 : t === 'PST+GST' ? 12 : 0 });
          }}
          size="small" sx={{ width: 110 }}>
          {taxTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
      </Box>

      <CataloguePickerModal open={catalogOpen} onClose={() => setCatalogOpen(false)} onAdd={handleAddFromCatalog} />
      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />

      <Snackbar open={snackbar.open} autoHideDuration={snackbar.severity === 'warning' ? 6000 : 2000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};
